import React, { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';

type Props = {
    className?: string;
    disabled?: boolean,
    properties: any,
    selection: any,
    setSelection: any
};

type Row =
    | { kind: 'all'; id: 'all'; label: string }
    | {
        kind: 'property';
        id: string;
        label: string;
        parentLabel: string;
        level: 0 | 1;
        meta: 'default' | 'scenario';
        groupId: string;
    };

type SelectableRow = Row;

const PropertySelect = ({
    className = '',
    disabled = false,
    properties,
    selection,
    setSelection
}: Props) => {

    const { parents, childrenByParent } = useMemo(() => {
        const parents = properties?.filter((p: any) => !p?.scenario?.parentId)?.sort((a: any, b: any) => a.name.localeCompare(b.name));

        const childrenByParent: Record<string, typeof properties> = {};
        if (properties && properties?.length > 0) {
            for (const p of properties) {
                if (!p?.scenario?.parentId) continue;
                (childrenByParent[p?.scenario?.parentId] ??= []).push(p);
            }
            for (const pid of Object.keys(childrenByParent)) {
                childrenByParent[pid]?.sort((a: any, b: any) => a.name.localeCompare(b.name));
            }
        }

        return { parents, childrenByParent };
    }, [properties]);

    const rows: SelectableRow[] = useMemo(() => {
        const out: SelectableRow[] = [];
        const parentProperties = properties?.filter((item: any) => !item?.scenario?.parentId)
        if (parentProperties?.length > 1) {
            out.push({ kind: 'all', id: 'all', label: 'All properties' });
        }
        if (parents && parents?.length > 0) {
            for (const parent of parents) {
                out.push({
                    kind: 'property',
                    id: parent.id,
                    label: parent.name,
                    parentLabel: parent.name,
                    level: 0,
                    meta: 'default',
                    groupId: parent.id,
                });

                const kids = childrenByParent[parent.id] ?? [];
                for (const c of kids) {
                    out.push({
                        kind: 'property',
                        id: c.id,
                        label: c.scenario?.name || '',
                        parentLabel: parent.name,
                        level: 1,
                        meta: 'scenario',
                        groupId: parent.id,
                    });
                }
            }
        }
        return out;
    }, [properties?.length, parents, childrenByParent]);

    const onlyOneProperty = properties?.length === 1;

    const selectedId = useMemo(() => {
        if (properties?.length === 0) return null;

        if (onlyOneProperty) return properties[0]!.id;

        if (selection?.type === 'all') {
            return rows.some((r) => r.kind === 'all') ? 'all' : rows.find((r) => r.kind === 'property')?.id ?? null;
        }

        const exists = properties?.some((p: any) => p?.id === selection?.id);
        if (exists) return selection.id;

        return rows?.some((r) => r.kind === 'all') ? 'all' : rows?.find((r) => r.kind === 'property')?.id ?? null;
    }, [onlyOneProperty, properties, rows, selection]);

    const selectedRow = useMemo(() => {
        if (!selectedId) return null;
        return rows?.find((r) => r.id === selectedId) ?? null;
    }, [rows, selectedId]);

    const isDisabled = disabled || properties?.length === 0 || onlyOneProperty;

    const onSelect = (row: SelectableRow) => {
        if (row.kind === 'all') setSelection({ type: 'all' });
        else setSelection({ type: 'property', id: row.id });
    };

    if (properties?.length === 0 || !selectedRow) {
        return (
            <div className={`w-full md:w-[320px] ${className}`}>
                <div
                    className={[
                        'w-full rounded-xl px-3 py-2.5 text-left text-sm',
                        'border border-gray-300/80 dark:border-white/15',
                        'bg-white/90 dark:bg-white/5 backdrop-blur',
                        'text-gray-500 dark:text-white/60',
                        'shadow-sm',
                        'opacity-60 cursor-not-allowed',
                    ].join(' ')}
                >
                    No properties
                </div>
            </div>
        );
    }

    const buttonTitle =
        selectedRow?.kind === 'all'
            ? selectedRow?.label
            : selectedRow?.meta === 'default'
                ? selectedRow?.label
                : selectedRow?.label;

    const buttonSubtitle =
        selectedRow?.kind === 'all'
            ? ''
            : selectedRow?.meta === 'default'
                ? 'Primary'
                : selectedRow?.parentLabel;

    const Badge = ({ text }: { text: string }) => (
        <span
            className={[
                'ml-3 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                'bg-black/5 text-gray-700',
                'dark:bg-white/10 dark:text-white/80',
            ].join(' ')}
        >
            {text}
        </span>
    );

    return (
        <div className={`w-full md:w-[320px] ${className} z-20`}>
            <Listbox value={selectedRow} onChange={onSelect} disabled={isDisabled}>
                <div className="relative">
                    <Listbox.Button
                        className={[
                            'w-full rounded-xl px-3 py-2.5 text-left text-sm',
                            'border border-gray-300/80 dark:border-white/15',
                            'bg-white/90 dark:bg-white/5 backdrop-blur',
                            'text-gray-900 dark:text-white',
                            'shadow-sm',
                            'focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/15',
                            isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                        ].join(' ')}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="truncate font-medium">{buttonTitle}</div>
                                <div className="truncate text-xs text-gray-500 dark:text-white/60">{buttonSubtitle}</div>
                            </div>

                            <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0 opacity-80">
                                <path
                                    d="M6 9L12 15L18 9"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                    </Listbox.Button>

                    <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Listbox.Options
                            className={[
                                'absolute z-40 mt-2 w-full overflow-hidden rounded-xl',
                                'border border-gray-200 dark:border-white/10',
                                'bg-white dark:bg-[#0b0b0f]',
                                'shadow-lg ring-1 ring-black/5 dark:ring-white/10',
                                'max-h-80 overflow-y-auto',
                                'text-gray-900 dark:text-white',
                                'py-1',
                            ].join(' ')}
                        >
                            {rows.map((row, idx) => {
                                const prev = rows[idx - 1];
                                const showDivider =
                                    idx > 0 &&
                                    row.kind !== 'all' &&
                                    prev?.kind !== 'all' &&
                                    row.groupId !== (prev as any)?.groupId &&
                                    row.level === 0;

                                if (showDivider) {
                                    // render divider + the row
                                }

                                const content = (
                                    <Listbox.Option
                                        key={`${row.kind}-${row.id}`}
                                        value={row}
                                        className={({ active }) =>
                                            [
                                                'cursor-pointer select-none',
                                                row.kind === 'property' && row.level === 1 ? 'pl-8 pr-3' : 'px-3',
                                                'py-2.5 text-sm',
                                                active ? 'bg-black/5 dark:bg-white/10' : '',
                                                'text-gray-900 dark:text-white',
                                            ].join(' ')
                                        }
                                    >
                                        {({ selected }) => (
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    {row.kind === 'property' && row.level === 1 ? (
                                                        <div className="relative">
                                                            {/* hierarchy hint: subtle line + dot */}
                                                            <span className="absolute left-[-14px] top-[9px] h-2 w-2 rounded-full bg-gray-300 dark:bg-white/20" />
                                                            {/* <span className="absolute left-[-10px] top-0 bottom-0 w-px bg-gray-200 dark:bg-white/10" /> */}
                                                            <div className="truncate font-medium">{row.label}</div>
                                                            <div className="truncate text-xs text-gray-500 dark:text-white/60">{row.parentLabel}</div>
                                                        </div>
                                                    ) : row.kind === 'property' ? (
                                                        <div className="truncate font-semibold">{row.label}</div>
                                                    ) : (
                                                        <div className="truncate font-medium">{row.label}</div>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    {row.kind === 'property' && row.meta === 'default' && <Badge text="Primary" />}
                                                    {row.kind === 'property' && row.meta === 'scenario' && <Badge text="Scenario" />}
                                                    {selected && (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0 opacity-90">
                                                            <path
                                                                d="M20 6L9 17l-5-5"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </Listbox.Option>
                                );

                                if (!showDivider) return content;

                                return (
                                    <div key={`wrap-${row.kind}-${row.id}`}>
                                        <div className="my-1 border-t border-gray-200/70 dark:border-white/10" />
                                        {content}
                                    </div>
                                );
                            })}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
        </div>
    );
};

export default PropertySelect;